import type { OrganizationBranding } from "./theme";

/**
 * UI_PALETTE_STOP_COUNT is the number of color stops a Mantine color scale carries (a
 * fixed 10-stop tuple). An organization's stored `ui` palette is a valid brand scale
 * only when it carries exactly this many stops.
 */
const UI_PALETTE_STOP_COUNT = 10;

/**
 * toBranding turns an organization's stored `theme.colors.ui` palette into the
 * OrganizationBranding the theme tints its primary scale with, or null when the org
 * carries no usable palette. Mantine's primary scale is a fixed 10-stop tuple, so a
 * palette that is not exactly ten non-empty stops — an org that never set a brand color,
 * or a partial/legacy payload — yields null and the theme keeps its default blue rather
 * than a padded or truncated scale. The input is the loosely-parsed string array the org
 * settings expose (`ui: string[]`), so both the Zod by-id read and the generated
 * publishable-key lookup feed it.
 */
export const toBranding = (ui: readonly string[] | undefined): OrganizationBranding | null => {
  if (!ui || ui.length !== UI_PALETTE_STOP_COUNT || ui.some((stop) => !stop)) {
    return null;
  }

  // The length + non-empty checks above verify a full ten stops at runtime; fix the
  // copy to the wire tuple the theme expects, mirroring OrgPreferencesPage's toTuple —
  // the established idiom for coercing a checked array to the 10-stop palette tuple.
  return {
    uiColors: [...ui] as [
      string,
      string,
      string,
      string,
      string,
      string,
      string,
      string,
      string,
      string,
    ],
  };
};
